/**
 * Native platform bootstrap (Capacitor).
 *
 * - Configures status bar to match app theme (dark BG, light text).
 * - Hides the splash screen once React has mounted.
 * - Registers a hardware back-button handler (Android) to prevent
 *   redirect loops and provide graceful app-exit on root screens.
 * - Initializes RevenueCat with the correct platform-specific API key,
 *   conditional on whether keys are present in the build environment.
 *
 * On the web (Capacitor.isNativePlatform() === false), this module is
 * effectively a no-op so the existing web Stripe flow is untouched.
 */
import { Capacitor } from "@capacitor/core";

export function isNative() {
  return Capacitor.isNativePlatform();
}

export function platform() {
  return Capacitor.getPlatform(); // "web" | "ios" | "android"
}

const ROOT_PATHS = new Set([
  "/", "/login", "/signup", "/onboarding",
  "/owner", "/shop", "/admin",
  "/privacy", "/terms", "/support",
]);

async function configureStatusBar() {
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setBackgroundColor({ color: "#09090B" });
    await StatusBar.setStyle({ style: Style.Light });
    // On iOS we want the WebView to extend under the status bar (edge-to-edge).
    // The web side adds `safe-top` padding so nothing overlaps. On Android
    // we already use translucent status bar via styles.xml, and overlay=true is
    // also the cleanest for Android 15+ edge-to-edge.
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch (e) {
    console.warn("StatusBar configure failed:", e);
  }
}

async function hideSplash() {
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch (e) {
    console.warn("SplashScreen hide failed:", e);
  }
}

/**
 * Register Android hardware back-button handler.
 * - On root paths: minimize the app (exit to home screen).
 * - On sub-pages with history: go back one step.
 * - On sub-pages without history: navigate to the role-appropriate dashboard.
 *
 * This prevents the redirect-loop issue where navigate(-1) bounces the user
 * between a redirect source and its destination indefinitely.
 */
async function registerBackButton() {
  if (platform() !== "android") return;
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("backButton", ({ canGoBack }) => {
      const path = window.location.pathname;
      if (ROOT_PATHS.has(path)) {
        // On a root screen, minimize the app instead of looping
        App.minimizeApp();
      } else if (canGoBack && window.history.length > 2) {
        window.history.back();
      } else {
        // No meaningful history; go to the dashboard
        window.location.replace("/owner");
      }
    });
  } catch (e) {
    console.warn("BackButton handler registration failed:", e);
  }
}

/**
 * One-time native init at app boot. Safe to call on web (no-op).
 */
export async function initNativeShell() {
  if (!isNative()) return;
  await configureStatusBar();
  registerBackButton();
  // Give React a beat to paint the first frame before tearing down the splash.
  setTimeout(() => { hideSplash(); }, 250);
}
